import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Upload, X, Zap, Loader2, Sparkles } from "lucide-react";

const ANNA_AVATAR = "https://images.unsplash.com/photo-1777927519810-5feb98777717?crop=entropy&cs=srgb&fm=jpg&q=85&w=120";
const SAM_AVATAR = "https://images.unsplash.com/photo-1656947296515-3faa76840890?crop=entropy&cs=srgb&fm=jpg&q=85&w=120";
const LEVELS = ["Class 1-4", "Class 5", "Class 6", "Class 7", "Class 8", "Class 9", "Class 10", "Class 11", "Class 12", "College", "Adult learner"];
const selCls = "w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50";

const Field = ({ label, children }) => <label className="block"><div className="label-mono mb-1.5">{label}</div>{children}</label>;

export function LessonForm({ options, balance, busy, onSubmit }) {
  const [f, setF] = useState({ topic: "", subject: "General", level: "Class 8", language: "en", length: "standard", presenter: "anna" });
  const [image, setImage] = useState(null);
  const cam = useRef(null), up = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const p = options?.pricing || {};
  const cost = (p[`learn_${f.length}`] || 0) + (image ? p.learn_photo || 0 : 0);
  const pickFile = (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Photo too large — max 6MB."); return; }
    const r = new FileReader(); r.onload = () => setImage(r.result); r.readAsDataURL(file);
  };
  const ok = (f.topic.trim() || image) && balance >= cost && !busy;

  return (
    <div className="space-y-5" data-testid="lesson-form">
      <Field label="Topic">
        <textarea data-testid="lesson-topic" rows={2} value={f.topic} onChange={set("topic")} placeholder="e.g. How does photosynthesis work?" className={`${selCls} resize-none`} />
      </Field>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={pickFile} data-testid="lesson-camera-input" />
        <input ref={up} type="file" accept="image/*" hidden onChange={pickFile} data-testid="lesson-upload-input" />
        <button data-testid="lesson-take-photo" onClick={() => cam.current.click()} className="rounded-full glass-card px-3.5 py-1.5 text-sm flex items-center gap-1.5 hover:border-mono-400/50"><Camera className="w-4 h-4" /> Take photo</button>
        <button data-testid="lesson-upload-photo" onClick={() => up.current.click()} className="rounded-full glass-card px-3.5 py-1.5 text-sm flex items-center gap-1.5 hover:border-mono-400/50"><Upload className="w-4 h-4" /> Upload photo</button>
        {image && (
          <div className="relative" data-testid="lesson-photo-preview">
            <img src={image} alt="lesson source" className="h-14 w-14 rounded-lg object-cover border border-white/10" />
            <button data-testid="lesson-photo-remove" onClick={() => setImage(null)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 flex items-center justify-center"><X className="w-3 h-3" /></button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Subject"><select data-testid="lesson-subject" value={f.subject} onChange={set("subject")} className={selCls}>{(options?.subjects || []).map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Class level"><select data-testid="lesson-level" value={f.level} onChange={set("level")} className={selCls}>{LEVELS.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Language"><select data-testid="lesson-language" value={f.language} onChange={set("language")} className={selCls}>{(options?.languages || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></Field>
        <Field label="Lesson length">
          <select data-testid="lesson-length" value={f.length} onChange={set("length")} className={selCls}>
            {(options?.lengths || []).map((l) => <option key={l.id} value={l.id}>{`${l.id[0].toUpperCase() + l.id.slice(1)} · ${l.scenes} scenes · ${p[`learn_${l.id}`]} cr`}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Presenter">
        <div className="flex gap-2">
          {[{ k: "anna", n: "Anna", av: ANNA_AVATAR }, { k: "sam", n: "Sam", av: SAM_AVATAR }].map((x) => (
            <button key={x.k} data-testid={`lesson-presenter-${x.k}`} onClick={() => set("presenter")(x.k)}
              className={`flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border transition-colors ${f.presenter === x.k ? "border-mono-400/60 bg-mono-400/10" : "border-white/10 hover:border-white/25"}`}>
              <img src={x.av} alt={x.n} className="w-8 h-8 rounded-full object-cover" /><span className="text-sm">{x.n}</span>
            </button>
          ))}
        </div>
      </Field>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 flex items-center justify-between" data-testid="lesson-cost-preview">
        <div className="text-sm text-slate-300 flex items-center gap-2"><Zap className="w-4 h-4 text-mono-300" /> This lesson costs <span data-testid="lesson-cost" className="text-mono-200">{cost}</span> credits{image ? " (incl. photo)" : ""}</div>
        <div className="text-xs text-slate-500">Balance {balance}</div>
      </div>
      {balance < cost && <div className="text-xs text-red-300" data-testid="lesson-insufficient">Not enough credits for this lesson.</div>}
      <button data-testid="lesson-generate" disabled={!ok} onClick={() => onSubmit({ ...f, image })}
        className="w-full rounded-full py-2.5 text-sm text-[#000000] flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate lesson · {cost} credits
      </button>
    </div>
  );
}
